import { create } from 'zustand';
import type { ChannelStatus } from '@conversia/shared-types';
import type { Channel } from '@/lib/types';

interface ChannelsState {
  items: Channel[];
  /** QR pendientes por canal (provienen del WebSocket channel.status). */
  qrByChannel: Record<string, string | undefined>;
  /** Mensaje de error por canal cuando la conexión falla. */
  errorByChannel: Record<string, string | undefined>;
  /**
   * Último estado en vivo por canal (del WebSocket channel.status). Es la fuente
   * que el QrModal observa para cerrarse solo al conectar: `items` no se mantiene
   * sincronizado en vivo (la lista la sirve react-query), así que el estado por
   * canal vive acá, keyed por id e independiente de `items`.
   */
  statusByChannel: Record<string, ChannelStatus | undefined>;

  setItems: (items: Channel[]) => void;
  upsert: (channel: Channel) => void;
  remove: (id: string) => void;
  setStatus: (id: string, status: ChannelStatus, qr?: string, error?: string) => void;
}

export const useChannelsStore = create<ChannelsState>((set) => ({
  items: [],
  qrByChannel: {},
  errorByChannel: {},
  statusByChannel: {},

  setItems: (items) => set({ items }),
  upsert: (channel) =>
    set((s) => {
      const idx = s.items.findIndex((c) => c.id === channel.id);
      if (idx === -1) return { items: [channel, ...s.items] };
      const next = s.items.slice();
      next[idx] = channel;
      return { items: next };
    }),
  remove: (id) => set((s) => ({ items: s.items.filter((c) => c.id !== id) })),
  setStatus: (id, status, qr, error) =>
    set((s) => ({
      items: s.items.map((c) => (c.id === id ? { ...c, status } : c)),
      qrByChannel: { ...s.qrByChannel, [id]: qr },
      errorByChannel: { ...s.errorByChannel, [id]: error },
      statusByChannel: { ...s.statusByChannel, [id]: status },
    })),
}));
